import { drawRect, movedBeyond } from '../drawRect.js';

const MIN_SIZE = 10;
const CLICK_TOLERANCE = 4;   // px the mouse may move during a press that is still a click

export class MouseDrawingAdapter {
    #$layer = null;
    #docId = null;
    #mediator = null;           // injected at attach (was a direct singleton import — L3 Entwirrung)
    #state = {
        startX:  null,
        startY:  null,
        dragBox: null,
        dragged: false,      // the press became a drag (published once as tag:draw:drag)
    };
    // While a box is drawn, the mouse is followed on the whole document, not only on the layer (plan
    // bugfix_2026-10-06_permission-tagging, phase polishing). Before, the layer alone listened: a mouse faster than the
    // box left the picture and the box stopped short of the edge; a button released outside never ended the drawing,
    // and the box went on following the mouse after coming back. Capture phase, so nothing on the way can swallow it.
    #onDocumentMove = (ev) => this.#onMouseMove(ev);
    #onDocumentUp   = (ev) => this.#onMouseUp(ev);

    // Binds mousedown to $layer ('.draw' namespace); move/up are followed on the document while drawing.
    attach($layer, docId = null, mediator = null) {
        this.#$layer = $layer;
        this.#docId = docId;
        this.#mediator = mediator;
        $layer.on('mousedown.draw', (ev) => this.#onMouseDown(ev));
    }

    detach() {
        this.#$layer?.off('.draw');
        this.#cleanup();
        this.#$layer = null;
    }

    isDrawing() { return this.#state.startX !== null; }

    #onMouseDown(ev) {
        if (!window.$(ev.target).hasClass('dragArea')) return;
        const $ = window.$;
        const offset = this.#$layer.offset();
        this.#state.startX = ev.clientX - offset.left + $(window).scrollLeft();
        this.#state.startY = ev.clientY - offset.top  + $(window).scrollTop();
        if (this.#state.dragBox == null) {
            this.#state.dragBox = $('<div/>', { class: 'tagBox' })
                .css({ left: this.#state.startX, top: this.#state.startY, width: 1, height: 1 });
            this.#$layer.append(this.#state.dragBox);
        }
        document.addEventListener('mousemove', this.#onDocumentMove, true);
        document.addEventListener('mouseup', this.#onDocumentUp, true);
        this.#mediator.publish('tag:draw:start', { docId: this.#docId });
        ev.preventDefault();
    }

    #onMouseMove(ev) {
        if (this.#state.startX == null || !this.#state.dragBox) return;
        try {
            const $ = window.$;
            const offset = this.#$layer.offset();
            const point = {
                x: ev.clientX - offset.left + $(window).scrollLeft(),
                y: ev.clientY - offset.top  + $(window).scrollTop(),
            };
            // The first move beyond the click tolerance makes the press a drag — announced once (the video pauses).
            if (!this.#state.dragged && movedBeyond({ x: this.#state.startX, y: this.#state.startY }, point, CLICK_TOLERANCE)) {
                this.#state.dragged = true;
                this.#mediator.publish('tag:draw:drag', { docId: this.#docId });
            }
            // Clamped to the layer on all four sides — outside the picture the box stands at the edge.
            const { l, t, w, h } = drawRect(
                { x: this.#state.startX, y: this.#state.startY }, point,
                { width: this.#$layer.width(), height: this.#$layer.height() },
            );

            this.#state.dragBox.css({
                left: l, top: t, width: w, height: h,
                'border-color': w > MIN_SIZE && h > MIN_SIZE ? '#C356A0' : '#f00',
            });
        } catch (err) {
            console.log('MouseDrawingAdapter mousemove: ' + err);
        }
        ev.preventDefault();
    }

    #onMouseUp(ev) {
        this.#stopFollowingDocument();
        this.#state.startX = this.#state.startY = null;
        if (!this.#state.dragBox) return;

        const w = this.#state.dragBox.width();
        const h = this.#state.dragBox.height();
        const dragged = this.#state.dragged;   // read before #cleanup resets it

        if (w > MIN_SIZE && h > MIN_SIZE) {
            const pos = this.#state.dragBox.position();
            const pix = { l: pos.left, t: pos.top, w, h };
            this.#cleanup();
            this.#mediator.publish('tag:draw:complete', { pix, docId: this.#docId });
            ev.preventDefault();
        } else if (!dragged) {
            // A click on the background (no drag): leave the box being edited; on video it plays/pauses.
            this.#cleanup();
            this.#mediator.publish('tag:layer:click', { docId: this.#docId });
            this.#mediator.publish('tag:draw:cancel', { docId: this.#docId });
        } else {
            // A drag that stayed too small for a box: no box, and no click either (the video stays paused).
            this.#cleanup();
            this.#mediator.publish('tag:draw:cancel', { docId: this.#docId });
        }
    }

    #stopFollowingDocument() {
        document.removeEventListener('mousemove', this.#onDocumentMove, true);
        document.removeEventListener('mouseup', this.#onDocumentUp, true);
    }

    #cleanup() {
        this.#stopFollowingDocument();
        this.#state.dragBox?.remove();
        this.#state.dragBox = null;
        this.#state.startX  = null;
        this.#state.startY  = null;
        this.#state.dragged = false;
    }
}
